import { Injectable } from '@angular/core';
import { StubService } from '../stub-service/stub.service';
import * as _ from 'lodash';

/** The configuration service namespace this app reads. One key; every value under it in one read. */
const CONFIG_NAMESPACE = 'MaterialsAppLegacy';
const CONFIG_STORAGE_KEY = 'appConfig';
const CONFIG_REFRESH_MS = 1000 * 60 * 60;

/**
 * A dashboard the menu offers.
 *
 * `Dashboard` is the directory under `/dashboards/` on the dashboards host. `TokenScheme` picks
 * the query the dashboard expects: `session` sends `SessionToken` and `bearerToken`, `temp` sends
 * `tempSessionToken` and `customerID`. `Params` are appended verbatim; a `true` value is a bare
 * flag, `'mobile'` is a flag only on a device.
 */
export interface DashboardRoute
{
	Name: string;
	Icon: string;
	Dashboard: string;
	TokenScheme?: 'session' | 'temp';
	Params?: { [key: string]: string | boolean };
}

export interface RoutingConfig
{
	Dashboards: Array<DashboardRoute>;
}

export interface AppConfig
{
	Routing: RoutingConfig;
}

/**
 * Exactly what the app did before it read any configuration. Every read merges over this, so no
 * row, an unreachable service, or a malformed row all resolve to the shipped behaviour.
 */
export const DEFAULT_APP_CONFIG: AppConfig =
{
	Routing:
	{
		Dashboards:
		[
			{ Name: 'Dashboard', Icon: 'fa-dashboard', Dashboard: 'materials_samples', TokenScheme: 'session',
				Params: { addNewSamples: true, hidePrinterButton: 'mobile' } },
			{ Name: 'Sample Plan Checklist', Icon: 'fa-list-alt', Dashboard: 'sampleplan_checklist', TokenScheme: 'session',
				Params: { addNewSamples: true, hidePrinterButton: 'mobile' } }
		]
	}
};

/**
 * Server-side configuration for this app, read from `Configuration/For/MaterialsAppLegacy`.
 *
 * The namespace is read whole - one request returns every value under it, keyed by value name -
 * so adding a second configurable area later is a new key in the same read, not a new request.
 *
 * Fails open in every direction: the defaults are the app's prior behaviour, the last good
 * configuration is kept in local storage for offline starts, and a value that does not validate
 * is replaced by its default rather than trusted.
 */
@Injectable()
export class AppConfigProvider
{
	private config: AppConfig = DEFAULT_APP_CONFIG;
	private lastLoadTime: number = 0;
	private loading: Promise<AppConfig> = null;

	constructor(private stub: StubService)
	{
		try
		{
			const tmpStored = window.localStorage.getItem(CONFIG_STORAGE_KEY);
			if (tmpStored)
			{
				this.config = this.normalise(JSON.parse(tmpStored));
			}
		}
		catch (err)
		{
			this.config = DEFAULT_APP_CONFIG;
		}
	}

	/**
	 * The configuration in hand. Never empty.
	 *
	 * @return {AppConfig}
	 */
	get(): AppConfig
	{
		return this.config;
	}

	/**
	 * The dashboards the menu should offer.
	 *
	 * @return {Array<DashboardRoute>}
	 */
	getDashboards(): Array<DashboardRoute>
	{
		return this.config.Routing.Dashboards;
	}

	/**
	 * Fetch the namespace from the server, if it has not been fetched recently.
	 *
	 * Concurrent callers share one request. A failed read leaves the current configuration in
	 * place; it never regresses a good one to the defaults.
	 *
	 * @param {boolean} [pForce] Ignore the refresh interval.
	 * @return {Promise<AppConfig>} The configuration after the attempt.
	 */
	async load(pForce?: boolean): Promise<AppConfig>
	{
		if (!pForce && (Date.now() - this.lastLoadTime) < CONFIG_REFRESH_MS)
		{
			return this.config;
		}
		if (this.loading)
		{
			return this.loading;
		}
		this.loading = (async () =>
		{
			try
			{
				const tmpResponse = await this.stub.do('get', 'Configuration', `/For/${ CONFIG_NAMESPACE }`, undefined, 'AppConfig');
				this.config = this.normalise(tmpResponse);
				this.lastLoadTime = Date.now();
				try
				{
					window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(this.config));
				}
				catch (err)
				{
				}
			}
			catch (err)
			{
				console.warn('App configuration not read; keeping what is in hand.', err && err.message ? err.message : err);
			}
			finally
			{
				this.loading = null;
			}
			return this.config;
		})();
		return this.loading;
	}

	/**
	 * Reduce whatever the server sent to a configuration the app can run on.
	 *
	 * Each area is validated on its own, so a broken `Routing` value does not cost a good one
	 * elsewhere, and a dashboard entry missing anything the page needs is dropped rather than
	 * offered. If the list comes out empty the defaults are used whole - a configuration that
	 * removes every dashboard is treated as no configuration, not as a request to remove the menu.
	 *
	 * @param {any} pRaw The response body.
	 * @return {AppConfig}
	 */
	normalise(pRaw: any): AppConfig
	{
		const tmpRaw = (pRaw && typeof(pRaw) === 'object') ? pRaw : {};
		const tmpRouting = (tmpRaw.Routing && typeof(tmpRaw.Routing) === 'object') ? tmpRaw.Routing : {};
		const tmpDashboards = _.filter(Array.isArray(tmpRouting.Dashboards) ? tmpRouting.Dashboards : [],
			(pEntry: any) => pEntry && typeof(pEntry) === 'object' &&
				typeof(pEntry.Name) === 'string' && pEntry.Name.trim() &&
				typeof(pEntry.Dashboard) === 'string' && /^[A-Za-z0-9_-]+$/.test(pEntry.Dashboard))
			.map((pEntry: any) => ({
				Name: String(pEntry.Name).trim(),
				Icon: (typeof(pEntry.Icon) === 'string' && pEntry.Icon.trim()) ? pEntry.Icon.trim() : 'fa-dashboard',
				Dashboard: pEntry.Dashboard,
				TokenScheme: (pEntry.TokenScheme === 'temp') ? 'temp' : 'session',
				Params: (pEntry.Params && typeof(pEntry.Params) === 'object') ? pEntry.Params : {}
			}));
		return {
			Routing:
			{
				Dashboards: tmpDashboards.length ? tmpDashboards : DEFAULT_APP_CONFIG.Routing.Dashboards
			}
		};
	}
}



// WEBPACK FOOTER //
// ./src/providers/app-config-service/app-config.service.ts